import { useState, useEffect } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { Clock, Music4 } from 'lucide-react';
import type { Tela } from '@/shared/types';

export function ConnectionDot({ isOnline }: { isOnline: boolean }) {
  return (
    <div className="flex items-center gap-1.5" title={isOnline ? 'Online' : 'Offline — cache local'}>
      {!isOnline && <span className="text-red-400 text-xs font-medium">Offline</span>}
      <span
        key={String(isOnline)}
        className={`w-2 h-2 rounded-full shrink-0 animate-scale-in ${isOnline ? 'bg-emerald-400 animate-pulse-slow' : 'bg-red-500 animate-pulse'}`}
      />
    </div>
  );
}

type IdleVariant = 'standby' | 'playlist_vazia';

const IDLE_COPY: Record<
  IdleVariant,
  { titulo: string; subtitulo: string; statusOnline: string; statusOffline: string }
> = {
  standby: {
    titulo: 'Fora do horário programado',
    subtitulo: 'A TV retoma automaticamente no próximo turno',
    statusOnline: 'Aguardando próximo turno',
    statusOffline: 'Offline · Cache ativo',
  },
  playlist_vazia: {
    titulo: 'Playlist vazia',
    subtitulo: 'Adicione músicas pelo painel para começar a tocar',
    statusOnline: 'Aguardando conteúdo',
    statusOffline: 'Offline',
  },
};

/**
 * Tela ociosa compartilhada — “Relógio de Palco”.
 * Standby e playlist vazia compartilham a mesma composição; só mudam copy + ícone.
 */
function PlayerIdleScreen({
  tela,
  isOnline,
  variant,
}: {
  tela: Tela;
  isOnline: boolean;
  variant: IdleVariant;
}) {
  const reduzir = useReducedMotion();
  const [time, setTime] = useState(() => new Date());
  const copy = IDLE_COPY[variant];
  const Icon = variant === 'playlist_vazia' ? Music4 : Clock;

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const hh = time.getHours().toString().padStart(2, '0');
  const mm = time.getMinutes().toString().padStart(2, '0');
  const dataExtenso = time.toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  });

  const content = (
    <div className="relative z-10 text-center select-none px-8 max-w-2xl mx-auto">
      {/* Relógio — âncora visual a distância */}
      <div className="font-sans text-[clamp(4.5rem,9vw,7rem)] font-bold text-slate-100 tabular-nums leading-none tracking-tight">
        {hh}
        <span
          className={`text-orange-500 ${reduzir ? '' : 'animate-pulse'}`}
          aria-hidden
        >
          :
        </span>
        {mm}
      </div>

      <p className="mt-3 text-[clamp(0.875rem,1.4vw,1.125rem)] text-white/55 capitalize">
        {dataExtenso}
      </p>

      {/* Ícone + mensagem por variante */}
      <div className="mt-10 mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-white/[0.08] bg-white/[0.03]">
        <Icon className="h-7 w-7 text-orange-400/80" strokeWidth={1.5} aria-hidden />
      </div>

      <p className="mt-5 text-[clamp(1.25rem,2.2vw,1.75rem)] font-semibold text-slate-100 line-clamp-1 max-w-xl mx-auto">
        {tela.nome}
      </p>
      <p className="mt-2 text-[clamp(1rem,1.6vw,1.25rem)] font-medium text-white/55">
        {copy.titulo}
      </p>
      <p className="mt-1.5 text-sm text-white/50 max-w-md mx-auto">
        {copy.subtitulo}
      </p>

      <div className="mt-10 flex items-center justify-center gap-2">
        <ConnectionDot isOnline={isOnline} />
        <span className={`text-xs ${isOnline ? 'text-emerald-400' : 'text-red-400'}`}>
          {isOnline ? copy.statusOnline : copy.statusOffline}
        </span>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-10 flex items-center justify-center overflow-hidden bg-[#0a0a0f]">
      {/* Orbs de palco — CSS; desligados via media query em reduced-motion */}
      <div
        className="pointer-events-none absolute inset-0 animate-standby-breathe"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 50%, rgba(var(--rgb-brand),0.07) 0%, transparent 70%)',
        }}
      />
      <div
        className="pointer-events-none absolute inset-0 animate-standby-breathe"
        style={{
          background:
            'radial-gradient(ellipse 60% 80% at 50% 50%, rgba(52,211,153,0.045) 0%, transparent 70%)',
          animationDelay: '3s',
        }}
      />

      {reduzir ? (
        content
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.32, 0.72, 0, 1] }}
        >
          {content}
        </motion.div>
      )}
    </div>
  );
}

export function PlaylistVaziaScreen({ tela, isOnline }: { tela: Tela; isOnline: boolean }) {
  return <PlayerIdleScreen tela={tela} isOnline={isOnline} variant="playlist_vazia" />;
}

export function StandbyScreen({ tela, isOnline }: { tela: Tela; isOnline: boolean }) {
  return <PlayerIdleScreen tela={tela} isOnline={isOnline} variant="standby" />;
}
